import React from 'react';
import { Twitter, Instagram, Youtube, Globe, Facebook } from 'lucide-react';

const AboutPage = () => {
  const socialLinks = [
    { name: 'Facebook', url: 'https://www.facebook.com/profile.php?id=100089106589635', icon: Facebook },
    { name: 'Twitter/X', url: 'https://x.com/SheLoves_Thee', icon: Twitter },
    { name: 'Instagram', url: 'https://www.instagram.com/sheloves_thee', icon: Instagram },
    { name: 'YouTube', url: 'https://www.youtube.com/@SheLoves_Thee', icon: Youtube },
    { name: 'Website', url: 'https://shelovesthee.com', icon: Globe },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="about-page">
      {/* Hero Section */}
      <div className="bg-[#001f3f] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            {/* Photo */}
            <div className="shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_hbcu-sports-hub/artifacts/nuwdhvnv_IMG_4570.jpeg" 
                alt="SheLovesThee Host"
                className="w-64 h-64 md:w-80 md:h-80 object-cover rounded-full border-4 border-[#DC2626] shadow-2xl"
                data-testid="about-photo"
              />
            </div>
            
            {/* Intro */}
            <div className="text-center md:text-left">
              <p className="text-lg text-[#DC2626] font-bold uppercase tracking-wider mb-2">
                Meet the Host
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase heading-font mb-4" data-testid="about-title">
                Timona Stapleton
              </h1>
              <p className="text-xl md:text-2xl text-slate-300 font-semibold mb-2">
                SheLovesThee Podcast
              </p>
              <p className="text-lg text-slate-300 mb-4">
                Jackson State Alumna | HBCU Sports Analyst | Content Creator
              </p>
              <p className="text-slate-400 mb-2">
                B.S. Business Administration - Marketing, Jackson State University
              </p>
              <p className="text-slate-400 text-sm">
                Broadcasting since April 2016 | 5.2M+ views
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bio Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white p-8 md:p-12 shadow-lg" data-testid="about-bio">
          <h2 className="text-3xl font-bold uppercase heading-font text-[#001f3f] mb-6">
            About SheLovesThee
          </h2>
          
          <div className="prose max-w-none text-slate-700 space-y-6">
            <p className="text-lg leading-relaxed">
              Welcome to the SheLovesThee Podcast: <strong>Your Premier Source for HBCU Sports, Culture, and Social Issues</strong>
            </p>
            
            <p className="leading-relaxed">
              Join us on the SheLovesThee Podcast, your go-to destination for the latest in HBCU sports as well as culture, and social issues. With a special emphasis on the SWAC Conference and all things Jackson State, our podcast provides insightful analysis and opinions grounded in factual information.
            </p>
            
            <p className="leading-relaxed">
              We are committed to focusing on HBCUs, highlighting the importance and contributions to sports and society. Our content is designed to be engaging and entertaining while providing historical context, expert analysis, and varying opinions from contributors.
            </p>

            <div className="bg-slate-50 p-6 rounded-lg border-l-4 border-[#DC2626]">
              <h3 className="text-xl font-bold text-[#001f3f] mb-4">Subscribe to our channel for:</h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <span className="text-[#DC2626]">•</span>
                  Comprehensive SWAC updates
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#DC2626]">•</span>
                  In-depth HBCU news
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#DC2626]">•</span>
                  Thoughtful discussions on social issues
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#DC2626]">•</span>
                  Exclusive interviews with coaches, student athletes and HBCU administration
                </li>
              </ul>
            </div>

            <p className="text-lg font-semibold text-[#001f3f]">
              Don't miss an episode—like and subscribe to ensure you never miss an update from SheLovesThee Podcast!
            </p>
          </div>
        </div>

        {/* Social Links */}
        <div className="mt-12 bg-[#001f3f] p-8 md:p-12 text-white" data-testid="about-social">
          <h2 className="text-3xl font-bold uppercase heading-font mb-8 text-center">
            Connect With Me
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {socialLinks.map((social) => {
              const IconComponent = social.icon;
              return (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-3 p-4 bg-white/10 hover:bg-[#DC2626] rounded-lg transition-all duration-300 group"
                  data-testid={`social-${social.name.toLowerCase()}`}
                >
                  <IconComponent className="h-8 w-8 group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-bold uppercase tracking-wider">{social.name}</span>
                </a>
              );
            })}
          </div>
        </div>

        {/* Jackson State Pride */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-4 bg-[#003366] text-white px-8 py-4 rounded-full">
            <span className="text-2xl">🐯</span>
            <span className="font-bold uppercase tracking-wider">Proud Jackson State Tiger</span>
            <span className="text-2xl">🐯</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
